// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngScriptInjector from "@gcg/App/Components/XngScriptInjector/XngScriptInjector";
import PopupContextProvider from "@gcg/App/Components/XPopup/XContext";
import { CustomAppState } from "@gcg/App/Store/Reducers";
import { screen } from "@gcg/XDL/Helpers";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import { PageProps as ContainerProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngHeadInjector from "@seacoastusa/App/Components/XngHeadInjector/XngHeadInjector";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    isSticky: state.xHeader.isSticky,
    stickyMenu: state.xHeader.stickyMenu,
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    header?: ContainerProps;
    container?: ContainerProps;
    navigation?: InjectableCss;
    navigationPlaceholder?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    header: {
        as: "header",
        fullWidth: [true, true, true, true, true],
        padding: 0,
        css: css`
            position: relative;
            z-index: 10;
        `,
    },
    navigation: {
        css: css`
            transition: top 350ms ease-in;
            background-color: ${getColor("text.main")};
            ${({ isSticky }: any) => isSticky && `position: fixed;top:-50px;width: 100%;`}
            ${({ stickyMenu }: any) => stickyMenu && `top:75px;`}
            ${screen("lte", "md")`
                display:none;
            `}
        `,
    },
    navigationPlaceholder: {
        css: css`
            ${({ isSticky }: any) => isSticky && `height:50px;`}
        `,
    },
    container: {
        as: "div",
        fullWidth: [true, true, false, false, false],
        padding: 0,
        background: "transparent",
        css: css`
            display: flex;
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isSticky,
    stickyMenu,
}: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <PopupContextProvider>
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            <Page {...styles.header} isHeader={true}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <Zone contentId={id} zoneName="GlobalWebsiteMessage" />
                <Zone contentId={id} zoneName="SecondaryNavigation" />
                <StyledWrapper {...styles.navigationPlaceholder} {...({ isSticky, stickyMenu } as any)} />
                <StyledWrapper {...styles.navigation} {...({ isSticky, stickyMenu } as any)}>
                    <Page {...styles.container}>
                        <Zone contentId={id} zoneName="MainNavigation" />
                    </Page>
                </StyledWrapper>
                <Zone contentId={id} zoneName="Breadcrumb" />
                <span ref={afterHeader} tabIndex={-1} />
                <XngScriptInjector />
                <XngHeadInjector />
            </Page>
        </PopupContextProvider>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
